import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Navigation, Search, Store, BadgeCheck, Loader2, CircleDot, Clock, Route, Heart, MessageSquare } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import BetaVendorBadge from "@/components/BetaVendorBadge";

// Format "HH:MM" 24h -> "H:MM AM/PM"
function to12h(hhmm) {
  if (!hhmm || !hhmm.includes(":")) return hhmm || "";
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DAY_LABELS_FULL = { sun: "Sunday", mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday" };

// Return e.g. "Opens tomorrow at 9:00 AM" or "Opens Monday at 9:00 AM" — searches next 7 days.
function nextOpenLabel(hours) {
  if (!hours) return null;
  const today = new Date().getDay();
  for (let offset = 1; offset <= 7; offset++) {
    const key = DAY_KEYS[(today + offset) % 7];
    const h = hours[key];
    if (h && !h.closed && h.open) {
      const prefix = offset === 1 ? "tomorrow" : DAY_LABELS_FULL[key];
      return `Opens ${prefix} at ${to12h(h.open)}`;
    }
  }
  return null;
}

// Derive today's open/closed status from a shop's location_hours + current local time.
// Returns { open: bool, label: string, urgent?: bool, sub?: string } or null if no hours info for today.
function derivedStatus(hours) {
  if (!hours || typeof hours !== "object") return null;
  const dayKey = DAY_KEYS[new Date().getDay()];
  const h = hours[dayKey];
  if (!h) return null;
  if (h.closed) return { open: false, label: "Closed today", sub: nextOpenLabel(hours) };
  if (!h.open || !h.close) return null;
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const [oh, om] = h.open.split(":").map(Number);
  const [ch, cm] = h.close.split(":").map(Number);
  const openMin = oh * 60 + (om || 0);
  const closeMin = ch * 60 + (cm || 0);
  if (nowMin < openMin) return { open: false, label: `Opens ${to12h(h.open)}` };
  if (nowMin >= closeMin) return { open: false, label: "Closed", sub: nextOpenLabel(hours) };
  const remaining = closeMin - nowMin;
  if (remaining <= 30) {
    return { open: true, urgent: true, label: `Closing in ${remaining} min` };
  }
  return { open: true, label: `Open until ${to12h(h.close)}` };
}

// Cake Pop Vendor pin — Cookie icon on coral (matches the Beta Vendor card).
// Verified bakers get a blue ring instead of white.
function cakePopIcon({ verified = false }) {
  const bg = "#FF7E67";
  const ring = verified ? "#2F80ED" : "#FFFFFF";
  const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' width='44' height='58' viewBox='0 0 44 58'>
      <defs>
        <filter id='shadow' x='-20%' y='-20%' width='140%' height='140%'>
          <feDropShadow dx='0' dy='2' stdDeviation='1.5' flood-color='rgba(0,0,0,0.35)'/>
        </filter>
      </defs>
      <line x1='22' y1='30' x2='22' y2='57' stroke='#8B5A2B' stroke-width='3' stroke-linecap='round' filter='url(#shadow)'/>
      <circle cx='22' cy='18' r='16' fill='${bg}' stroke='${ring}' stroke-width='2.5' filter='url(#shadow)'/>
      <g transform='translate(13 9)' stroke='#FFFFFF' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' fill='none'>
        <circle cx='9' cy='9' r='8.2'/>
        <circle cx='6.4' cy='6.4' r='0.6' fill='#FFFFFF'/>
        <circle cx='12' cy='11.6' r='0.6' fill='#FFFFFF'/>
        <circle cx='9' cy='9' r='0.6' fill='#FFFFFF'/>
        <circle cx='8.3' cy='12.7' r='0.6' fill='#FFFFFF'/>
        <circle cx='5.3' cy='10.5' r='0.6' fill='#FFFFFF'/>
      </g>
    </svg>`;
  return L.divIcon({
    className: "cakepop-marker",
    html: svg,
    iconSize: [44, 58],
    iconAnchor: [22, 57],
    popupAnchor: [0, -52],
  });
}

// Helper component: pan/zoom map when props change
function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom ?? map.getZoom(), { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

// Farm Stand Vendor pin — Wheat icon on green (matches the Beta Vendor card).
function farmStandIcon() {
  const bg = "#8EAC50";
  const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' width='44' height='58' viewBox='0 0 44 58'>
      <defs>
        <filter id='shadow2' x='-20%' y='-20%' width='140%' height='140%'>
          <feDropShadow dx='0' dy='2' stdDeviation='1.5' flood-color='rgba(0,0,0,0.35)'/>
        </filter>
      </defs>
      <line x1='22' y1='30' x2='22' y2='57' stroke='#8B5A2B' stroke-width='3' stroke-linecap='round' filter='url(#shadow2)'/>
      <circle cx='22' cy='18' r='16' fill='${bg}' stroke='#FFFFFF' stroke-width='2.5' filter='url(#shadow2)'/>
      <g transform='translate(11 7) scale(0.92)' stroke='#FFFFFF' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' fill='none'>
        <path d='M2 22 16 8'/>
        <path d='M3.47 12.53 5 11l1.53 1.53a3.5 3.5 0 0 1 0 4.94L5 19l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z'/>
        <path d='M7.47 8.53 9 7l1.53 1.53a3.5 3.5 0 0 1 0 4.94L9 15l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z'/>
        <path d='M11.47 4.53 13 3l1.53 1.53a3.5 3.5 0 0 1 0 4.94L13 11l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z'/>
        <path d='M20 2h2v2a4 4 0 0 1-4 4h-2V6a4 4 0 0 1 4-4Z'/>
        <path d='M11.47 17.47 13 19l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L5 19l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z'/>
        <path d='M15.47 13.47 17 15l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L9 15l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z'/>
        <path d='M19.47 9.47 21 11l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L13 11l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z'/>
      </g>
    </svg>`;
  return L.divIcon({
    className: "farmstand-marker",
    html: svg,
    iconSize: [44, 58],
    iconAnchor: [22, 57],
    popupAnchor: [0, -52],
  });
}

// Wraps any base icon with a small orange "POP-UP" ribbon on top — used when a baker
// has activated a temporary pop-up pin for a market or event.
function popupBadgedIcon(baseIconFactory) {
  const inner = baseIconFactory();
  const innerHtml = inner.options.html;
  const html = `
    <div style="position:relative;width:${inner.options.iconSize[0]}px;height:${inner.options.iconSize[1] + 14}px;">
      <div style="position:absolute;top:0;left:50%;transform:translateX(-50%);background:#F4A83B;color:#3B2C24;font-size:8.5px;font-weight:800;letter-spacing:1px;padding:2px 6px;border-radius:999px;border:1.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);z-index:2;white-space:nowrap;">POP-UP</div>
      <div style="position:absolute;top:14px;left:0;">${innerHtml}</div>
    </div>`;
  return L.divIcon({
    className: "popup-marker",
    html,
    iconSize: [inner.options.iconSize[0], inner.options.iconSize[1] + 14],
    iconAnchor: [inner.options.iconAnchor[0], inner.options.iconAnchor[1] + 14],
    popupAnchor: [0, -inner.options.iconSize[1] + 4],
  });
}

export default function FindABaker() {
  const { user } = useAuth();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [flyTo, setFlyTo] = useState(null);
  const [userPos, setUserPos] = useState(null);   // true GPS position — drives the blue "you are here" dot
  const [zipCenter, setZipCenter] = useState(null); // ZIP-code search center — used ONLY for distance sort, never drawn as a dot
  const [locating, setLocating] = useState(false);
  const geoWatchRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [radius, setRadius] = useState(0); // 0 = any distance
  const [sortBy, setSortBy] = useState("closest"); // closest | recent | alpha
  const [chips, setChips] = useState({ open: false, near10: false, keto: false, vegan: false, gf: false, favorites: false });
  const [vendorFilter, setVendorFilter] = useState("all"); // all | cake_pops | farm_stand
  const markerRefs = useRef({});
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const targetShopParam = searchParams.get("shop");
  const [, setTick] = useState(0);

  // Re-render every 60s so the "Open until" / "Closing in X min" labels stay current.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get("/marketplace/map").then((r) => setShops(r.data)).catch(() => toast.error("Could not load bakers")).finally(() => setLoading(false));
  }, []);

  // Guest-email capture: when an anonymous visitor clicks the heart, ask for their email
  // so we can send them broadcasts from that baker.
  const [guestModal, setGuestModal] = useState(null);  // { shop, email, submitting } | null
  const [contactModal, setContactModal] = useState(null);  // { shop, name, email, message, submitting } | null

  // Contact button — logged-in members go straight to DMs, guests get a modal
  const openContact = (shop) => {
    if (user && user !== false) {
      nav(`/messages/${shop.user_id}`);
      return;
    }
    setContactModal({ shop, name: "", email: "", message: "", submitting: false });
  };

  const submitContact = async () => {
    if (!contactModal) return;
    const name = (contactModal.name || "").trim();
    const email = (contactModal.email || "").trim().toLowerCase();
    const message = (contactModal.message || "").trim();
    if (!name) { toast.error("What's your name?"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid email"); return; }
    if (message.length < 5) { toast.error("Add a short message"); return; }
    setContactModal((m) => ({ ...m, submitting: true }));
    try {
      await api.post("/marketplace/map/contact", { baker_id: contactModal.shop.user_id, name, email, message });
      toast.success("Message sent — the baker will reply to your email.");
      setContactModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the message");
      setContactModal((m) => ({ ...m, submitting: false }));
    }
  };

  const toggleFavorite = async (shop) => {
    if (!user || user === false) {
      // Show email capture modal instead
      setGuestModal({ shop, email: "", submitting: false });
      return;
    }
    // Optimistic update
    setShops((prev) => prev.map((s) => s.user_id === shop.user_id ? { ...s, is_favorited: !s.is_favorited } : s));
    try {
      await api.post(`/marketplace/map/favorites/${shop.user_id}`);
    } catch {
      // Rollback
      setShops((prev) => prev.map((s) => s.user_id === shop.user_id ? { ...s, is_favorited: shop.is_favorited } : s));
      toast.error("Could not save favorite");
    }
  };

  const submitGuestSubscribe = async () => {
    if (!guestModal) return;
    const email = (guestModal.email || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid email"); return; }
    setGuestModal((g) => ({ ...g, submitting: true }));
    try {
      await api.post("/marketplace/map/guest-subscribe", { baker_id: guestModal.shop.user_id, email });
      toast.success(`You'll get updates from ${guestModal.shop.business_name || "@" + guestModal.shop.username}`);
      setGuestModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't sign you up");
      setGuestModal((g) => ({ ...g, submitting: false }));
    }
  };

  // Auto-focus target shop from ?shop=username query param (e.g., after scanning a QR code)
  useEffect(() => {
    if (!targetShopParam || !shops.length) return;
    const target = shops.find((s) => s.username === targetShopParam);
    if (target) {
      focusShop(target);
    }
    // eslint-disable-next-line
  }, [targetShopParam, shops]);

  const initialCenter = useMemo(() => {
    if (shops.length) return [shops[0].latitude, shops[0].longitude];
    return [39.8283, -98.5795]; // geographic center of continental US
  }, [shops]);

  // Haversine distance in miles
  const distance = (a, b) => {
    if (!a || !b) return null;
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 3958.8; // miles
    const dLat = toRad(b[0] - a[0]);
    const dLon = toRad(b[1] - a[1]);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const isZipQuery = /^\d{5}(-\d{4})?$/.test(q);
    let list = shops;
    if (q && !isZipQuery) {
      list = list.filter((s) =>
        (s.business_name || "").toLowerCase().includes(q) ||
        (s.city || "").toLowerCase().includes(q) ||
        (s.state || "").toLowerCase().includes(q) ||
        (s.location_note || "").toLowerCase().includes(q)
      );
    }
    // Filter chips
    if (chips.open) list = list.filter((s) => derivedStatus(s.location_hours)?.open);
    if (vendorFilter !== "all") list = list.filter((s) => (s.vendor_type || "cake_pops") === vendorFilter);
    if (chips.keto) list = list.filter((s) => (s.dietary_tags || []).includes("keto"));
    if (chips.vegan) list = list.filter((s) => (s.dietary_tags || []).includes("vegan"));
    if (chips.gf) list = list.filter((s) => (s.dietary_tags || []).includes("gluten-free"));
    if (chips.favorites) list = list.filter((s) => s.is_favorited);
    // Distance calculations use GPS if available, else the ZIP-code centroid (never a hardcoded fallback).
    const refPos = userPos || zipCenter;
    if (refPos) {
      list = list.map((s) => ({ ...s, _dist: distance(refPos, [s.latitude, s.longitude]) }));
      if (radius > 0) list = list.filter((s) => s._dist <= radius);
      if (chips.near10) list = list.filter((s) => s._dist <= 10);
    } else if (chips.near10) {
      // Nudge user to enable geolocation for distance filter
      list = [];
    }
    // Sorting
    if (sortBy === "closest" && refPos) {
      list = [...list].sort((a, b) => (a._dist ?? 9999) - (b._dist ?? 9999));
    } else if (sortBy === "recent") {
      list = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    } else if (sortBy === "alpha") {
      list = [...list].sort((a, b) => (a.business_name || "").localeCompare(b.business_name || ""));
    }
    return list;
  }, [shops, query, userPos, zipCenter, radius, chips, sortBy, vendorFilter]);

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported in this browser"); return; }
    setLocating(true);
    // First get a quick fix so the map moves right away…
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = [pos.coords.latitude, pos.coords.longitude];
        setUserPos(loc);
        setFlyTo({ center: loc, zoom: 12 });
        setLocating(false);
        toast.success("Located you — sorted by distance");
      },
      (err) => {
        setLocating(false);
        setUserPos(null); // permission denied → make sure the blue dot stays hidden
        toast.error(err.message || "Could not get your location");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    // …then keep the dot live so it follows the user (great for outdoor market-day discovery).
    if (geoWatchRef.current == null) {
      try {
        geoWatchRef.current = navigator.geolocation.watchPosition(
          (pos) => setUserPos([pos.coords.latitude, pos.coords.longitude]),
          () => { /* silent — the initial getCurrentPosition already surfaced any error */ },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
        );
      } catch { /* watchPosition not supported — the one-shot fix above is enough */ }
    }
  };

  // Stop watching the browser's GPS when the user leaves the page — no background battery drain.
  useEffect(() => () => {
    if (geoWatchRef.current != null && navigator.geolocation?.clearWatch) {
      try { navigator.geolocation.clearWatch(geoWatchRef.current); } catch { /* ignore */ }
      geoWatchRef.current = null;
    }
  }, []);

  // Detect a US-style ZIP code (12345 or 12345-6789)
  const zipMatch = query.trim().match(/^(\d{5})(-\d{4})?$/);
  const searchByZip = async () => {
    if (!zipMatch) return;
    const zip = zipMatch[1];
    setLocating(true);
    try {
      // zippopotam.us is a free, reliable ZIP-code → lat/lng service (no auth, no rate limits for casual use)
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`, { headers: { "Accept": "application/json" } });
      if (!res.ok) {
        toast.error(`Couldn't find location for ZIP ${zip}`);
      } else {
        const data = await res.json();
        const place = data.places && data.places[0];
        if (!place) {
          toast.error(`Couldn't find location for ZIP ${zip}`);
        } else {
          const loc = [parseFloat(place.latitude), parseFloat(place.longitude)];
          setZipCenter(loc); // used ONLY for distance sorting — does NOT plant a blue "you are here" dot
          setFlyTo({ center: loc, zoom: 10 });
          toast.success(`Showing bakers near ${place["place name"]}, ${place["state abbreviation"]} (ZIP ${zip})`);
        }
      }
    } catch {
      toast.error("Couldn't reach the geocoder — try again");
    }
    setLocating(false);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (zipMatch) searchByZip();
  };

  const focusShop = (shop) => {
    setActiveId(shop.user_id);
    setFlyTo({ center: [shop.latitude, shop.longitude], zoom: 12 });
    const m = markerRefs.current[shop.user_id];
    if (m) setTimeout(() => m.openPopup(), 600);
  };

  return (
    <>
      <Header />
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 lg:py-6">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              <MapPin className="w-3.5 h-3.5" style={{ color: "#FF7E67" }} /><span>Locator</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              Find a <span style={{ color: "#FF7E67" }}>Local Stand</span>
            </h1>
            <p className="mt-1 text-[#857163] max-w-2xl text-sm">
              Discover local bakers and farm stands near you.
            </p>
            {user && user !== false && (
              <Link
                to="/find-a-baker/settings"
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#FF7E67] hover:underline"
                data-testid="manage-my-finder-info"
              >
                <MapPin className="w-3 h-3" /> Manage my finder info →
              </Link>
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <form onSubmit={onSearchSubmit} className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#857163]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by shop, city, state, or ZIP…"
                className="cp-input !pl-9 !py-2 !w-64 !text-sm !pr-20"
                data-testid="map-search"
              />
              {zipMatch && (
                <button
                  type="submit"
                  disabled={locating}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                  style={{ background: "#2F80ED" }}
                  data-testid="zip-go-btn"
                  title={`Show bakers near ZIP ${zipMatch[1]}`}
                >
                  {locating ? "…" : "Go"}
                </button>
              )}
            </form>
            <button
              onClick={locateMe}
              disabled={locating}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 disabled:opacity-70"
              style={{ background: "#2F80ED" }}
              data-testid="find-near-me-btn"
            >
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
              Find near me
            </button>
            <select
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              disabled={!userPos && !zipCenter}
              title={(userPos || zipCenter) ? "Filter by distance" : "Click 'Find near me' or search by ZIP first"}
              className="cp-input !py-2 !text-sm !w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="radius-select"
            >
              <option value={0}>Any distance</option>
              <option value={5}>Within 5 mi</option>
              <option value={10}>Within 10 mi</option>
              <option value={25}>Within 25 mi</option>
              <option value={50}>Within 50 mi</option>
              <option value={100}>Within 100 mi</option>
            </select>
          </div>
        </div>

        {/* Vendor type segmented control */}
        <div className="mb-3 inline-flex bg-white border-2 border-[#E7DED5] rounded-full p-1" data-testid="vendor-filter-group">
          {[
            { key: "all", label: "All Vendors", accent: "#3B2C24" },
            { key: "cake_pops", label: "Cake Pop Stands", accent: "#FF7E67" },
            { key: "farm_stand", label: "Farm Stands", accent: "#B91C1C" },
          ].map((v) => {
            const active = vendorFilter === v.key;
            return (
              <button
                key={v.key}
                onClick={() => setVendorFilter(v.key)}
                className="px-3.5 py-1.5 rounded-full text-xs font-semibold transition"
                style={active
                  ? { background: v.accent, color: "#fff" }
                  : { background: "transparent", color: "#3B2C24" }}
                data-testid={`vendor-filter-${v.key}`}
              >
                {v.label}
              </button>
            );
          })}
        </div>

        {/* Filter chips + sort */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          {[
            { key: "open", label: "Open Now", activeStyle: { background: "#8EAC50", color: "#fff", borderColor: "#8EAC50" } },
            { key: "near10", label: "< 10 mi", activeStyle: { background: "#2F80ED", color: "#fff", borderColor: "#2F80ED" } },
            { key: "keto", label: "Keto Friendly", activeStyle: { background: "#F4A83B", color: "#3B2C24", borderColor: "#F4A83B" } },
            { key: "vegan", label: "Vegan Options", activeStyle: { background: "#8EAC50", color: "#fff", borderColor: "#8EAC50" } },
            { key: "gf", label: "Gluten-free", activeStyle: { background: "#B79885", color: "#fff", borderColor: "#B79885" } },
            { key: "favorites", label: "❤ Favorites", activeStyle: { background: "#FF7E67", color: "#fff", borderColor: "#FF7E67" } },
          ].map((c) => {
            const active = chips[c.key];
            return (
              <button
                key={c.key}
                onClick={() => setChips({ ...chips, [c.key]: !active })}
                className="px-3 py-1.5 rounded-full text-xs font-semibold border transition"
                style={active ? c.activeStyle : { background: "#fff", color: "#3B2C24", borderColor: "#E7DED5" }}
                data-testid={`filter-chip-${c.key}`}
              >
                {c.label}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2">
            <label className="text-xs text-[#857163]">Sort:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="cp-input !py-1.5 !text-xs !w-auto"
              data-testid="sort-select"
            >
              <option value="closest">Closest</option>
              <option value="recent">Recently Added</option>
              <option value="alpha">A – Z</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[360px_1fr] lg:h-[calc(100vh-260px)] lg:min-h-[560px]">
          {/* Sidebar list */}
          <aside className="cp-card p-0 overflow-hidden flex flex-col order-2 lg:order-none h-[380px] lg:h-auto">
            <div className="px-4 py-3 border-b border-[#EBDCD1] flex items-center justify-between shrink-0">
              <div className="text-sm font-medium text-[#3B2C24]">
                {loading ? "Loading…" : `${filtered.length} baker${filtered.length === 1 ? "" : "s"}`}
              </div>
              {(userPos || zipCenter) && <span className="text-[10px] text-[#857163] uppercase tracking-wide">sorted by distance</span>}
            </div>
            <div className="overflow-y-auto flex-1" data-testid="map-shop-list">
              {loading ? (
                <div className="p-8 grid place-items-center text-[#857163]"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#857163]">
                  <Store className="w-8 h-8 mx-auto opacity-40 mb-2" />
                  {shops.length === 0 ? "No bakers on the map yet." : "No shops match your search."}
                </div>
              ) : (
                filtered.map((s) => {
                  const refPos = userPos || zipCenter;
                  const dist = refPos ? (s._dist ?? distance(refPos, [s.latitude, s.longitude])) : null;
                  const status = derivedStatus(s.location_hours);
                  return (
                    <div
                      key={s.user_id}
                      onClick={() => focusShop(s)}
                      className={`w-full text-left px-4 py-3 border-b border-[#EBDCD1]/60 hover:bg-[#F9EFE6] transition cursor-pointer relative ${activeId === s.user_id ? "bg-[#F9EFE6]" : ""}`}
                      data-testid={`shop-item-${s.username}`}
                    >
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(s); }}
                        className="absolute top-2.5 right-2.5 w-8 h-8 grid place-items-center rounded-full hover:bg-white/70 transition z-10"
                        aria-label={s.is_favorited ? "Remove from favorites" : "Add to favorites"}
                        data-testid={`fav-btn-${s.username}`}
                      >
                        <Heart className="w-4 h-4" fill={s.is_favorited ? "#FF7E67" : "none"} stroke={s.is_favorited ? "#FF7E67" : "#B79885"} strokeWidth={2} />
                      </button>
                      <div className="flex items-start gap-3 pr-8">
                        {s.avatar_url ? (
                          <img src={fileUrl(s.avatar_url)} alt={s.business_name} className="w-11 h-11 rounded-full object-cover shrink-0 border-2 border-white shadow-sm" />
                        ) : (
                          <div className="w-11 h-11 rounded-full grid place-items-center shrink-0 text-white font-heading text-lg border-2 border-white shadow-sm" style={{ background: s.theme_accent || "#FF7E67" }}>
                            {(s.business_name || s.username)[0].toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-heading text-base text-[#3B2C24] font-semibold leading-tight truncate">
                            {s.business_name}
                            {s.verified && <BadgeCheck className="inline-block ml-1 w-3.5 h-3.5 align-[-2px]" style={{ color: "#2F80ED" }} />}
                            {s.is_beta_tester && <span className="ml-1 align-middle"><BetaVendorBadge program={s.beta_program} size="xs" /></span>}
                          </div>
                          {s.tagline && <div className="text-xs text-[#857163] truncate mt-0.5">{s.tagline}</div>}
                          <div className="text-[11px] text-[#857163] mt-1 flex items-center gap-1.5 flex-wrap">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{[s.city, s.state].filter(Boolean).join(", ")}</span>
                            {status && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold shrink-0"
                                style={status.urgent
                                  ? { background: "rgba(244,168,59,0.20)", color: "#B26A00" }
                                  : status.open
                                    ? { background: "rgba(142,172,80,0.20)", color: "#4A6B1F" }
                                    : { background: "rgba(220,38,38,0.15)", color: "#B91C1C" }}
                                data-testid={`open-status-${s.username}`}
                              >
                                <CircleDot className="w-2.5 h-2.5" /> {status.label}
                              </span>
                            )}
                            {dist != null && <span className="ml-auto shrink-0 font-semibold text-[#3B2C24]">{dist < 10 ? dist.toFixed(1) : Math.round(dist)} mi</span>}
                          </div>
                          {(s.dietary_tags || []).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {(s.dietary_tags || []).slice(0, 3).map((t) => (
                                <span key={t} className="text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-[#F4A83B]/15 text-[#B26A00]" data-testid={`tag-${s.username}-${t}`}>
                                  {t.replace("-", " ")}
                                </span>
                              ))}
                            </div>
                          )}
                          {s.location_note && <div className="text-[11px] text-[#3B2C24] mt-1 italic line-clamp-2" data-testid={`note-${s.username}`}>{s.location_note}</div>}
                          {status?.sub && <div className="text-[10px] text-[#857163] mt-0.5" data-testid={`sub-${s.username}`}>{status.sub}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </aside>

          {/* Map */}
          <div className="rounded-3xl overflow-hidden border border-[#EBDCD1] relative order-1 lg:order-none h-[70vh] min-h-[440px] lg:h-auto lg:min-h-0" data-testid="baker-map">
            <MapContainer center={initialCenter} zoom={shops.length ? 4 : 4} scrollWheelZoom style={{ width: "100%", height: "100%" }}>
              <TileLayer
                attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {shops.map((s) => {
                const status = derivedStatus(s.location_hours);
                return (
                <Marker
                  key={s.user_id}
                  position={[s.latitude, s.longitude]}
                  icon={s.popup_active
                    ? popupBadgedIcon(() => (s.vendor_type === "farm_stand" ? farmStandIcon() : cakePopIcon({ verified: s.verified })))
                    : (s.vendor_type === "farm_stand" ? farmStandIcon() : cakePopIcon({ verified: s.verified }))}
                  ref={(el) => { if (el) markerRefs.current[s.user_id] = el; }}
                  eventHandlers={{ click: () => setActiveId(s.user_id) }}
                >
                  <Popup>
                    <div className="min-w-[220px]">
                      <div className="-mx-3 -mt-3 mb-2 h-24 overflow-hidden rounded-t-lg relative" style={{ background: s.theme_accent || "#FF7E67" }}>
                        {s.finder_logo_url ? (
                          <img src={fileUrl(s.finder_logo_url)} alt={s.business_name} className="w-full h-full object-cover" />
                        ) : s.avatar_url ? (
                          <img src={fileUrl(s.avatar_url)} alt={s.business_name} className="w-full h-full object-cover object-center" />
                        ) : (
                          <div className="w-full h-full grid place-items-center text-white font-heading text-4xl opacity-95">
                            {(s.business_name || s.username)[0].toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="font-heading text-base font-semibold text-[#3B2C24] leading-tight">
                        {s.business_name}
                        {s.verified && <BadgeCheck className="inline-block ml-1 w-3.5 h-3.5 align-[-2px]" style={{ color: "#2F80ED" }} />}
                        {s.is_beta_tester && <span className="ml-1 align-middle"><BetaVendorBadge program={s.beta_program} size="xs" /></span>}
                      </div>
                      {s.tagline && <div className="text-xs text-[#857163] mt-0.5">{s.tagline}</div>}
                      <div className="text-[11px] text-[#857163] mt-1 flex items-center gap-1.5 flex-wrap">
                        <MapPin className="w-3 h-3" /><span>{[s.city, s.state].filter(Boolean).join(", ")}</span>
                        {status && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                            style={status.urgent
                              ? { background: "rgba(244,168,59,0.20)", color: "#B26A00" }
                              : status.open
                                ? { background: "rgba(142,172,80,0.20)", color: "#4A6B1F" }
                                : { background: "rgba(220,38,38,0.15)", color: "#B91C1C" }}
                          >
                            <CircleDot className="w-2.5 h-2.5" /> {status.label}
                          </span>
                        )}
                      </div>
                      {(s.dietary_tags || []).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {(s.dietary_tags || []).map((t) => (
                            <span key={t} className="text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-[#F4A83B]/15 text-[#B26A00]">
                              {t.replace("-", " ")}
                            </span>
                          ))}
                        </div>
                      )}
                      {s.location_note && <div className="text-xs text-[#3B2C24] mt-1.5 italic border-l-2 pl-2" style={{ borderColor: s.theme_accent || "#FF7E67" }}>{s.location_note}</div>}
                      {s.vendor_type === "farm_stand" && s.currently_carrying && (
                        <div className="mt-2 rounded-lg px-2 py-1.5 text-[11px]" style={{ background: "#FFF5F5", border: "1px solid #B91C1C33", color: "#7F1D1D" }} data-testid={`popup-carrying-${s.username}`}>
                          <span className="font-bold uppercase tracking-wide text-[9px]" style={{ color: "#B91C1C" }}>Currently carrying:</span> {s.currently_carrying}
                        </div>
                      )}
                      {s.popup_active && (
                        <div className="mt-2 rounded-lg px-2 py-1.5 text-[11px]" style={{ background: "#FFF9EF", border: "1px solid #F4A83B55", color: "#B26A00" }} data-testid={`popup-badge-${s.username}`}>
                          <div className="flex items-center gap-1 mb-0.5">
                            <span className="font-bold uppercase tracking-wide text-[9px]" style={{ color: "#B26A00" }}>Pop-up today</span>
                            {s.popup_expires_at && (
                              <span className="text-[10px] text-[#857163]">· ends {new Date(s.popup_expires_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                            )}
                          </div>
                          {s.popup_note && <div className="text-[11px] text-[#3B2C24]">{s.popup_note}</div>}
                        </div>
                      )}
                      {status?.sub && <div className="text-[11px] text-[#857163] mt-1.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {status.sub}</div>}
                      {(s.finder_menu || []).filter((m) => m?.image_url).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-[#EBDCD1]">
                          <div className="text-[10px] uppercase tracking-widest text-[#857163] font-semibold mb-1.5">Menu</div>
                          <div className="grid grid-cols-3 gap-1" data-testid={`popup-menu-${s.username}`}>
                            {(s.finder_menu || []).filter((m) => m?.image_url).slice(0, 6).map((m, idx) => {
                              const hasStock = typeof m.available_quantity === "number";
                              const soldOut = m.sold_out;
                              return (
                                <div key={idx} className="relative group">
                                  <img
                                    src={fileUrl(m.image_url)}
                                    alt={m.description || `Menu item ${idx + 1}`}
                                    title={m.description || ""}
                                    className={`w-full aspect-square object-cover rounded ${soldOut ? "grayscale opacity-70" : ""}`}
                                  />
                                  {soldOut ? (
                                    <div className="absolute bottom-1 left-1 right-1 text-[8px] font-bold uppercase tracking-wider text-white bg-[#C2185B]/90 rounded px-1 py-0.5 text-center pointer-events-none" data-testid={`menu-stock-${s.username}-${idx}`}>
                                      Sold out
                                    </div>
                                  ) : hasStock ? (
                                    <div className="absolute bottom-1 left-1 right-1 text-[9px] font-bold text-white bg-black/70 rounded px-1 py-0.5 text-center pointer-events-none" data-testid={`menu-stock-${s.username}-${idx}`}>
                                      {m.available_quantity} in stock
                                    </div>
                                  ) : null}
                                  {m.description && (
                                    <div className="absolute inset-0 bg-black/70 text-white text-[9px] p-1 rounded flex items-center justify-center text-center leading-tight opacity-0 group-hover:opacity-100 transition">
                                      {m.description}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {s.storefront_enabled && (
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                          <Link
                            to={`/shop/${s.username}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold text-white"
                            style={{ background: s.theme_accent || "#FF7E67" }}
                            data-testid={`popup-shop-${s.username}`}
                          >
                            <Store className="w-3 h-3" /> Shop
                          </Link>
                          {(s.payment_venmo || s.payment_zelle || s.payment_cashapp) && (
                            <div className="flex items-center gap-1 flex-wrap" data-testid={`popup-pay-${s.username}`}>
                              {s.payment_venmo && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#EAF3FE", color: "#1D5FBF" }} title="Venmo">Venmo · @{s.payment_venmo.replace(/^@/, "")}</span>
                              )}
                              {s.payment_cashapp && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#E7F7EA", color: "#177E3B" }} title="CashApp">CashApp · ${s.payment_cashapp.replace(/^\$/, "")}</span>
                              )}
                              {s.payment_zelle && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#F1EBFF", color: "#5B21B6" }} title="Zelle">Zelle · {s.payment_zelle}</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${s.latitude},${s.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                          style={{ color: "#2F80ED" }}
                          data-testid={`popup-directions-${s.username}`}
                        >
                          Get directions <Route className="w-3 h-3" />
                        </a>
                        <button
                          onClick={() => openContact(s)}
                          className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                          style={{ color: "#8EAC50" }}
                          data-testid={`popup-contact-${s.username}`}
                        >
                          <MessageSquare className="w-3 h-3" /> Contact baker
                        </button>
                        <button
                          onClick={() => toggleFavorite(s)}
                          className="inline-flex items-center gap-1 text-xs font-semibold hover:underline ml-auto"
                          style={{ color: s.is_favorited ? "#FF7E67" : "#857163" }}
                          data-testid={`popup-fav-${s.username}`}
                        >
                          <Heart className="w-3 h-3" fill={s.is_favorited ? "#FF7E67" : "none"} />
                          {s.is_favorited ? "Saved" : "Save"}
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );})}
              {userPos && (
                <Marker
                  position={userPos}
                  icon={L.divIcon({
                    className: "user-marker",
                    html: `<div style="width:18px;height:18px;border-radius:50%;background:#2F80ED;border:3px solid #fff;box-shadow:0 0 0 3px rgba(47,128,237,0.35);"></div>`,
                    iconSize: [18, 18],
                    iconAnchor: [9, 9],
                  })}
                >
                  <Popup>You are here</Popup>
                </Marker>
              )}
              <FlyTo center={flyTo?.center} zoom={flyTo?.zoom} />
            </MapContainer>
          </div>
        </div>
      </main>
      {guestModal && (
        <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" data-testid="guest-subscribe-modal" onClick={() => !guestModal.submitting && setGuestModal(null)}>
          <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-3xl mb-2">📣</div>
            <div className="font-heading text-xl text-[#3B2C24]">Get updates from this baker</div>
            <div className="text-sm text-[#857163] mt-1">
              We'll email you when <b>{guestModal.shop.business_name || "@" + guestModal.shop.username}</b> shares a new drop or pop-up. Unsubscribe anytime.
            </div>
            <input
              type="email"
              autoFocus
              value={guestModal.email}
              onChange={(e) => setGuestModal((g) => ({ ...g, email: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && submitGuestSubscribe()}
              placeholder="you@example.com"
              className="cp-input mt-4"
              data-testid="guest-subscribe-email"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setGuestModal(null)}
                disabled={guestModal.submitting}
                className="flex-1 px-4 py-2 rounded-full text-sm border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6] disabled:opacity-50"
                data-testid="guest-subscribe-cancel"
              >
                Not now
              </button>
              <button
                onClick={submitGuestSubscribe}
                disabled={guestModal.submitting || !guestModal.email}
                className="flex-1 cp-btn-primary disabled:opacity-60"
                data-testid="guest-subscribe-submit"
              >
                {guestModal.submitting ? "Sending…" : "Get updates"}
              </button>
            </div>
            <div className="mt-3 text-center text-[10px] text-[#857163]">
              We'll only email you when this baker posts. No other marketing.
            </div>
          </div>
        </div>
      )}
      {contactModal && (
        <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" data-testid="contact-modal" onClick={() => !contactModal.submitting && setContactModal(null)}>
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-5 h-5 text-[#8EAC50]" />
              <div className="font-heading text-xl text-[#3B2C24]">Message {contactModal.shop.business_name || "@" + contactModal.shop.username}</div>
            </div>
            <div className="text-sm text-[#857163] mt-1 mb-4">
              We'll email your message to the baker. They'll reply directly to the email address you provide.
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs text-[#857163]">Your name</span>
                <input
                  value={contactModal.name}
                  onChange={(e) => setContactModal((m) => ({ ...m, name: e.target.value }))}
                  className="cp-input mt-1"
                  placeholder="Jane Baker"
                  data-testid="contact-name"
                />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Your email</span>
                <input
                  type="email"
                  value={contactModal.email}
                  onChange={(e) => setContactModal((m) => ({ ...m, email: e.target.value }))}
                  className="cp-input mt-1"
                  placeholder="you@example.com"
                  data-testid="contact-email"
                />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Message</span>
                <textarea
                  value={contactModal.message}
                  onChange={(e) => setContactModal((m) => ({ ...m, message: e.target.value.slice(0, 1000) }))}
                  rows={4}
                  className="cp-input mt-1"
                  placeholder="Hi! Do you take orders for a Saturday pickup?"
                  data-testid="contact-message"
                />
                <div className="text-right text-[10px] text-[#857163] mt-0.5">{1000 - contactModal.message.length} chars left</div>
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setContactModal(null)}
                disabled={contactModal.submitting}
                className="flex-1 px-4 py-2 rounded-full text-sm border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6] disabled:opacity-50"
                data-testid="contact-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitContact}
                disabled={contactModal.submitting}
                className="flex-1 cp-btn-primary disabled:opacity-60"
                data-testid="contact-submit"
              >
                {contactModal.submitting ? "Sending…" : "Send message"}
              </button>
            </div>
            <div className="mt-3 text-center text-[10px] text-[#857163]">
              Site members can also <Link to="/login" className="underline">sign in</Link> to message this baker in-app.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
